<template>
  <page-layout>
    <template #header>
      <div class="bg-white border-b border-gray-200">
        <div class="space-y-8 max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div class="lg:flex lg:items-center lg:justify-between">
            <div class="flex-1 min-w-0">
              <h2
                class="
                  text-2xl
                  font-bold
                  leading-7
                  text-light-blue-900
                  sm:text-3xl sm:truncate
                "
              >
                {{
                  t("quotation_title", {
                    number: quotation.data.id,
                  })
                }}
              </h2>
              <span
                class="mt-1"
                :class="[
                  quotationStatusClasses[quotation.data.status_color],
                  'inline-flex items-center px-3 py-0.5 rounded-full font-medium capitalize',
                ]"
              >
                {{ t(quotation.data.status) }}
              </span>
              <div
                class="
                  mt-1
                  flex flex-col
                  sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6
                "
              >
                <div class="mt-2 flex items-center text-sm text-gray-500">
                  <UserIcon
                    class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  {{ quotation.data.user.name }}
                </div>
                <div
                  v-if="quotation.data.customer"
                  class="mt-2 flex items-center text-sm text-gray-500"
                >
                  <OfficeBuildingIcon
                    class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  {{ quotation.data.customer.name }}
                </div>
                <div class="mt-2 flex items-center text-sm text-gray-500">
                  <CalendarIcon
                    class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  {{
                    t("last_update", {
                      date: quotation.data.formatted_updated_at,
                    })
                  }}
                </div>
              </div>
            </div>
            <div class="mt-5 flex lg:mt-0 lg:ml-4">
              <span class="hidden sm:block">
                <Link
                  :href="
                    route('quotations.edit', {
                      quotation: quotation.data.id,
                    })
                  "
                >
                  <button
                    type="button"
                    class="
                      inline-flex
                      items-center
                      px-4
                      py-2
                      border border-gray-300
                      rounded-md
                      shadow-sm
                      text-sm
                      font-medium
                      text-gray-700
                      bg-white
                      hover:bg-gray-50
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-light-blue-500
                    "
                  >
                    <PencilIcon
                      class="-ml-1 mr-2 h-5 w-5 text-gray-500"
                      aria-hidden="true"
                    />
                    {{ t("edit") }}
                  </button>
                </Link>
              </span>

              <span class="hidden sm:block ml-3">
                <button
                  @click="updatingQuotationStatus = true"
                  type="button"
                  class="
                    inline-flex
                    items-center
                    px-4
                    py-2
                    border border-gray-300
                    rounded-md
                    shadow-sm
                    text-sm
                    font-medium
                    text-gray-700
                    bg-white
                    hover:bg-gray-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-light-blue-500
                  "
                >
                  <PencilAltIcon
                    class="-ml-1 mr-2 h-5 w-5 text-gray-500"
                    aria-hidden="true"
                  />
                  {{ t("update_status") }}
                </button>
              </span>

              <span class="sm:ml-3">
                <a
                  target="_blank"
                  :href="
                    route('pdf-quotations.generatePDF', {
                      quotation: quotation.data.id,
                    })
                  "
                >
                  <button
                    type="button"
                    class="
                      inline-flex
                      items-center
                      px-4
                      py-2
                      border border-transparent
                      rounded-md
                      shadow-sm
                      text-sm
                      font-medium
                      text-white
                      bg-light-blue-600
                      hover:bg-light-blue-700
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-light-blue-500
                    "
                  >
                    <DocumentDownloadIcon
                      class="-ml-1 mr-2 h-5 w-5"
                      aria-hidden="true"
                    />
                    {{ t("get_pdf") }}
                  </button>
                </a>
              </span>

              <!-- Dropdown -->
              <Menu as="span" class="ml-3 relative sm:hidden">
                <MenuButton
                  class="
                    inline-flex
                    items-center
                    px-4
                    py-2
                    border border-gray-300
                    rounded-md
                    shadow-sm
                    text-sm
                    font-medium
                    text-gray-700
                    bg-white
                    hover:bg-gray-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-light-blue-500
                  "
                >
                  {{ t("more") }}
                  <ChevronDownIcon
                    class="-mr-1 ml-2 h-5 w-5 text-gray-500"
                    aria-hidden="true"
                  />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="
                      origin-top-right
                      absolute
                      right-0
                      mt-2
                      -mr-1
                      w-48
                      rounded-md
                      shadow-lg
                      py-1
                      bg-white
                      ring-1 ring-black ring-opacity-5
                      focus:outline-none
                    "
                  >
                    <MenuItem v-slot="{ active }">
                      <button
                        @click="updatingQuotationStatus = true"
                        type="button"
                        :class="[
                          active ? 'bg-gray-100' : '',
                          'block px-4 py-2 text-sm text-gray-700',
                        ]"
                      >
                        {{ t("update_status") }}
                      </button>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <Link
                        :href="
                          route('quotations.edit', {
                            quotation: quotation.data.id,
                          })
                        "
                      >
                        <div
                          :class="[
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700',
                          ]"
                        >
                          {{ t("edit") }}
                        </div>
                      </Link>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <div class="space-y-8 max-w-7xl mx-auto py-10 sm:px-6 lg:px-8 -mt-10">
        <div class="bg-white shadow-md px-4 py-5 sm:p-6">
          <div class="w-full grid md:grid-cols-2 gap-3">
            <div class="col-span-2">
              <div
                v-for="(concept, index) in quotation.data.quotationConcepts
                  .data"
                :key="concept.id"
                class="w-full grid md:grid-cols-6 gap-3"
              >
                <div class="flex col-span-3 items-center space-x-3">
                  <ui-input
                    class="w-full"
                    disabled
                    type="text"
                    :label="t('concept')"
                    :value="concept.concept"
                  ></ui-input>
                </div>

                <ui-input
                  disabled
                  type="text"
                  :label="t('quantity')"
                  :value="concept.quantity"
                ></ui-input>

                <ui-input
                  disabled
                  type="text"
                  :label="t('unitary_price')"
                  :value="concept.unitary_price / 100"
                ></ui-input>

                <ui-input
                  disabled
                  type="text"
                  :label="t('tax %')"
                  :value="concept.tax / 100"
                ></ui-input>

                <div class="col-span-6 flex space-x-3 w-full">
                  <ui-input
                    disabled
                    type="text"
                    :label="t('area')"
                    :value="concept.physical_service_area"
                    v-if="concept.physicalService"
                  ></ui-input>

                  <ui-input
                    disabled
                    type="text"
                    :label="t('type')"
                    :value="concept.physical_service_type"
                    v-if="concept.physicalService"
                  ></ui-input>

                  <ui-textarea
                    disabled
                    class="flex-1"
                    type="text"
                    :value="concept.description"
                    :label="t('description')"
                  ></ui-textarea>
                </div>

                <ui-divider
                  v-if="
                    index < quotation.data.quotationConcepts.data.length - 1
                  "
                  class="my-3 col-span-6"
                ></ui-divider>
              </div>
            </div>

            <ui-divider class="my-6 col-span-2"></ui-divider>

            <div class="flex justify-end col-span-2">
              <div
                class="
                  mr-4
                  text-right text-sm text-gray-500
                  flex flex-col
                  justify-between
                "
              >
                <p>Subtotal</p>
                <p>Impuestos</p>
                <p class="text-lg">Total</p>
              </div>

              <div
                class="
                  text-right
                  flex flex-col
                  justify-between
                  space-y-2
                  text-gray-700
                "
              >
                <p>
                  {{ quotationData.formatted_subtotal }}
                </p>
                <p>
                  {{ quotationData.formatted_taxes }}
                </p>
                <p class="text-xl text-light-blue-900 font-bold">
                  {{ quotationData.formatted_total }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <teleport to="#modals">
      <quotation-status-update
        @toggleVisibility="updatingQuotationStatus = !updatingQuotationStatus"
        :on="updatingQuotationStatus"
        :quotation="quotation"
        :quotationStatuses="quotationStatuses"
      />
    </teleport>
  </page-layout>
</template>

<script>
import PageHeader from "./../Shared/Organisms/PageHeader";
import AppLayout from "./../Layouts/AppLayout";
import { useI18n } from "vue-i18n";
import PageLayout from "./../Layouts/PageLayout";
import {
  UserIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  LinkIcon,
  OfficeBuildingIcon,
  PencilIcon,
  DocumentDownloadIcon,
} from "@heroicons/vue/solid";
import { PencilAltIcon } from "@heroicons/vue/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import QuotationStatusUpdate from "./Quotations/QuotationStatusUpdate.vue";

const quotationStatusClasses = {
  gray: "bg-gray-100 text-gray-800",
  lightBlue: "bg-light-blue-100 text-light-blue-800",
  indigo: "bg-indigo-100 text-indigo-800",
  blue: "bg-blue-100 text-blue-800",
  red: "bg-red-100 text-red-800",
};

export default {
  layout: AppLayout,

  props: ["quotation", "quotationStatuses", "quotationData"],

  setup() {
    const { t } = useI18n();
    return { t, quotationStatusClasses };
  },

  components: {
    PageLayout,
    PageHeader,
    AppLayout,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    UserIcon,
    CalendarIcon,
    CheckIcon,
    ChevronDownIcon,
    LinkIcon,
    OfficeBuildingIcon,
    PencilIcon,
    QuotationStatusUpdate,
    DocumentDownloadIcon,
    PencilAltIcon,
  },

  data() {
    return {
      updatingQuotationStatus: false,
    };
  },
};
</script>

<i18n>
{
  "es": {
    "quotation_title": "Cotización {number}",
    "last_update": "Actualizada {date}",
    "description": "Descripción",
    "quantity": "Cantidad",
    "concept": "Concepto",
    "concepts": "Conceptos",
    "unitary_price": "Precio unitario",
    "tax %": "Impuestos %",
    "area": "Area",
    "type": "Tipo de medición",
    "tax %": "Impuestos %",
    "pdf": "Generar PDF",
    "opportunity": "Oportunidad",
    "negotiation": "Negociación",
    "closing": "Cerrando",
    "confirmed": "Confirmada",
    "rejected": "Rechazada",
    "update_status": "Actualizar estado",
    "get_pdf": "PDF",
    "edit": "Editar",
    "more": "Más"
  }
}
</i18n>
